import "amazon-connect-streams";
import type { WithMetricsOptions } from "@expert/cloudwatch-metrics";
import type { ExWoMetricData, MetricsToServerProps } from "@expert/cloudwatch-metrics/src/sendMetrics";
import type { Partner } from "@expert/shared-types";
import type { ExpertWorkspaceWebSocket } from "@expert/websocket";
import type { JsonValue } from "type-fest";

import { getAppConfigSingleton } from "@expert/app-config";
import { sendEventMetric, sendMetrics, withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger, type Logger } from "@expert/logging";
import difference from "lodash.difference";

import type { ChatEventCallbacks, ChatMessage, ChatMessagesCache } from "@/sdk/agent/chat";
import type { Task } from "@/sdk/agent/task";
import type { AdvanceReadStatusOptions, SurveyMeta } from "@/sdk/agent/types";
import type { ConferenceParticipant, UpdateParticipantOptions } from "@/sdk/agent/voice/conferenceTypes";
import type { CallSdk } from "@/sdk/call/callSdk";

import { getRootDispatcher } from "@/analytics/rootDispatcher";
import { useBlockedChatStore } from "@/blocked-chat-banner/blockedChat.store";
import { getCallDetails } from "@/call-details/api";
import { env } from "@/config";
import { clearCallbackState, hasPendingCallback, sdkEventBus } from "@/sdk";
import { AgentSdkBase } from "@/sdk/agent/agentSdkBase";
import { TASK_COMPLETE_REASON } from "@/sdk/agent/config";
import { useAgentStore } from "@/sdk/agent/store/agent.store";
import {
    type AgentActivity,
    type CallbackState,
    isLocale,
    type MutableAgentAttribute,
    type TaskCompletedReason,
} from "@/sdk/agent/types";
import { assertVoiceTask, isVoiceTask } from "@/sdk/agent/voice/typeGuards";
import { EvlCallSdk } from "@/sdk/call/evlCallSdk";
import { DisclosureContactNotReadyError, DisclosureInFlightError } from "@/sdk/errorTypes";
import { LogFlow } from "@/sdk/logging/flows";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { userCache } from "@/shared-utils";
import { sendSurveyData } from "@/surveys";
import { SurveyRequest } from "@/surveys/api/types";

import getAgentStateByActivity from "./agent/getAgentStateByActivity";
import getAgentStateByType from "./agent/getAgentStateByType";
import isMessagingAuxBlockedInChat from "./agent/isMessagingAuxBlockedInChat";
import { agentStateForLog } from "./agentStateForLog";
import AmazonConnectContact, { getAmazonContactConfig } from "./AmazonConnectContact";
import AmazonConnectChatContact from "./chat/AmazonConnectChatContact";
import { AmazonConnectChatSDK, ContactEndedError } from "./chat/amazonConnectChatSdk";
import { endChatContact } from "./chat/utils/contactStateUtils";
import { assertAmazonConnectChatTask, isAmazonConnectChatTask, isChatContact } from "./chat/utils/typeGuards";
import { playDigits } from "./dtmf";
import normalizePartner from "./normalizePartner";
import { extractRoutingProfileInfo } from "./types/routingProfile";
import AmazonConnectVoiceContact from "./voice/AmazonConnectVoiceContact";
import { buildEndpointFromArn } from "./voice/buildEndpointFromArn";
import { contactToParticipants, contactToQuickConnectConnections } from "./voice/contactToParticipants";
import { fetchEndpointMaps } from "./voice/endpointToQuickConnectMap";
import hasCustomerLeft from "./voice/hasCustomerLeft";
import { shouldPlayDisconnectChime } from "./voice/shouldPlayDisconnectChime";
import withCallbacks from "./withCallbacks";

async function onHoldState(contact: connect.Contact, connection: connect.BaseConnection, state: boolean) {
    await new Promise<void>((resolve, reject) => {
        const subscription = contact.onRefresh(() => {
            try {
                if (connection.isOnHold() !== state) return;
                subscription.unsubscribe();
                resolve();
            } catch (cause) {
                reject(new Error("Failed to detect on-hold change", { cause }));
            }
        });
    });
}

function getParticipantCallIds(participants: ConferenceParticipant[]) {
    return participants.reduce((set, { participantCallId: id }) => set.add(id), new Set<string>());
}

/**
 * In the chat build ExWo owns only chat contacts; voice/callback is handled outside ExWo
 * (CCP widget / voice app). Ignoring non-chat contacts here prevents a concurrent voice contact
 * from stealing the workspace's session selection (#6973) and from triggering a voice
 * session-summary send in the chat app. Voice audio is unaffected — it's the CCP iframe, which
 * is independent of ExWo's session model.
 */
export function shouldIgnoreContact(contact: connect.Contact): boolean {
    return env.channel === "chat" && !isChatContact(contact);
}

/** True when the agent state name is one of Amazon Connect's error/miss states (e.g. MissedCallAgent). */
export function isAgentErrorState(stateName: string | undefined): boolean {
    if (stateName === undefined) return false;
    return (Object.values(connect.AgentErrorStates ?? {}) as string[]).includes(stateName);
}

/** Terminal contact states that keep the agent occupied until the contact is cleared. */
function isTerminalContactState(stateType: connect.ContactStateType | undefined): boolean {
    return (
        stateType === connect.ContactStateType.MISSED ||
        stateType === connect.ContactStateType.REJECTED ||
        stateType === connect.ContactStateType.ERROR
    );
}

/**
 * How long to wait after the agent enters an error state before confirming a chat block by re-checking
 * for a still-terminal ignored contact (#6973). A transient failure (abandoned outbound) clears its
 * contact within milliseconds, so waiting lets it resolve; a real miss leaves the contact terminal.
 */
const BLOCKED_CHAT_DEBOUNCE_MS = 3_000;

/**
 * Filters contacts by state, separating contacts to clear from valid contacts.
 */
export function getFilteredContacts(contacts: connect.Contact[]): {
    contactsToClear: connect.Contact[];
    filteredContacts: connect.Contact[];
} {
    try {
        const { true: contactsToClear = [], false: filteredContacts = [] } = Object.groupBy(contacts, (contact) => {
            const stateType = contact.getState().type;
            const shouldClear =
                stateType === connect.ContactStateType.MISSED ||
                stateType === connect.ContactStateType.REJECTED ||
                stateType === connect.ContactStateType.ERROR;
            return `${shouldClear}`;
        });
        return { contactsToClear, filteredContacts };
    } catch (error) {
        // TODO @asurion-core/expert-workspace-platform to see if app can actually continue here
        AmazonConnectAgentSDK.logger.warn({ error }, "Failed to get filtered contacts");
        return { contactsToClear: [], filteredContacts: contacts };
    }
}

/** A Mindful (VHT) callback is an inbound contact carrying the `VHTCaller="1"` attribute. */
export function isMindfulCallback(contact: connect.Contact): boolean {
    return contact.isInbound() && contact.getAttributes()?.VHTCaller?.value === "1";
}

/**
 * Acknowledge a Mindful (VHT) callback by sending DTMF "1" on the expert's behalf so the customer
 * connects without a manual key-press (#6864). Call once the contact is CONNECTED (the whisper only
 * accepts the tone on a live connection) and confirmed a Mindful callback via {@link isMindfulCallback}.
 * Best-effort: logs and swallows any failure so it never throws into the connect flow.
 */
export async function acceptMindfulCallback(contact: connect.Contact, logger: Logger): Promise<void> {
    try {
        logger.info({ contactId: contact.contactId }, 'Auto-acknowledging Mindful callback with DTMF "1"');
        await withCallbacks((callbacks) => contact.getAgentConnection().sendDigits("1", callbacks), {
            logger,
            action: "acceptMindfulCallback",
            logContext: { contactId: contact.contactId },
        });
    } catch (error) {
        logger.warn({ error, contactId: contact.contactId }, "Failed to auto-acknowledge Mindful callback");
    }
}

const defaultConnectMetricsDimensions = { communicationProvider: "AmazonConnect", channel: env.channel };

/**
 * Wrapper around `withMetrics` that injects `channel: env.channel` into every
 * emission. Use this instead of `withMetrics` directly for any timing inside
 * the Amazon Connect SDK so the dashboard's channel filter (voice vs chat)
 * applies uniformly to all metrics originating here.
 */
function withConnectMetrics<T>(actionName: string, action: () => Promise<T>, options: WithMetricsOptions) {
    return withMetrics(actionName, action, {
        ...options,
        dimensions: { ...options.dimensions, ...defaultConnectMetricsDimensions },
    });
}

/**
 * Filters tasks before initialization, separating any that should be completed immediately.
 */
function filterTasksForInit(tasks: AmazonConnectContact[]): {
    tasksToInit: AmazonConnectContact[];
    tasksToComplete: AmazonConnectContact[];
} {
    const tasksToInit: AmazonConnectContact[] = [];
    const tasksToComplete: AmazonConnectContact[] = [];

    for (const task of tasks) {
        //TODO: see if this is safe to be done for all connect tasks
        // Voice tasks and non-wrapping tasks always pass through
        if (!isAmazonConnectChatTask(task) || task.status !== "wrapping") {
            tasksToInit.push(task);
            continue;
        }

        // Chat task in wrapping - check if expired for edge case resolution
        const session = useSessionStore.getState().getSessionByTaskId(task.id);
        const wrappingState = session?.wrappingState;

        // no wrapping state found in store upon rehydration or is already expired
        const isExpired = !wrappingState || wrappingState.expirationTimestamp <= Date.now();

        if (isExpired) {
            if (wrappingState) {
                useSessionStore.getState().clearWrappingState(session!.id);
            }
            tasksToComplete.push(task);
        } else {
            tasksToInit.push(task);
        }
    }

    return { tasksToInit, tasksToComplete };
}

const contactToTaskCompletedReason = new WeakMap<connect.Contact, TaskCompletedReason>();

export interface CreateOptions {
    allowFramedSoftphone?: boolean;
    vdiPlatform?: string;
    secondaryCCPUrl?: string;
    enableGlobalResiliency?: boolean;
    /** Gates the Mindful callback auto-accept (see acceptMindfulCallbackIfNeeded). Resolved once at init. */
    autoAcceptMindfulCallbacks?: boolean;
    /**
     * Gates the chat-build non-chat-contact ignore (#6973). Resolved once at init; defaults to false
     * (ships dark) unless the flag resolves true, so rollout is driven entirely by config. When false,
     * voice contacts are processed as before. Only ever gates chat-build code (paired with
     * `shouldIgnoreContact`), so it has no effect on the voice build.
     */
    isNonChatContactIgnoreEnabled?: boolean;
    loginUrl?: string;
    attemptKey?: string;
    retries?: number;
    factor?: number;
    metricsHandler: typeof sendMetrics;
    onAuthRedirect?: VoidFunction;
    loginPopup?: boolean;
    loginPopupAutoClose?: boolean;
    /**
     * The AWS region of the Amazon Connect instance (e.g. 'us-east-1').
     * Required for ChatJS to make properly authenticated API calls.
     */
    connectRegion: string;
    /** External deps needed for chat operations */
    chatDependencies?: {
        chatEventCallbacks: ChatEventCallbacks;
        chatMessagesCache: ChatMessagesCache;
    };
}

export default class AmazonConnectAgentSDK extends AgentSdkBase<AmazonConnectContact> {
    public readonly serviceProvider = "connect";
    private static readonly SDK_NAME = "AmazonConnectAgentSDK";
    readonly sdkType = AmazonConnectAgentSDK.SDK_NAME;
    readonly requiresEvl = false;

    static readonly logger = getLogger({ module: AmazonConnectAgentSDK.SDK_NAME });
    static async create(
        container: HTMLElement,
        ccpUrl: string,
        partner: string,
        employeeNum: string | null,
        {
            allowFramedSoftphone = false,
            vdiPlatform,
            secondaryCCPUrl,
            enableGlobalResiliency = false,
            autoAcceptMindfulCallbacks = false,
            isNonChatContactIgnoreEnabled = false,
            loginUrl,
            attemptKey = "com.amazon.connect.login.attempts",
            retries = 3,
            factor = 2,
            metricsHandler,
            onAuthRedirect,
            loginPopup,
            loginPopupAutoClose = true,
            connectRegion,
            chatDependencies,
        }: CreateOptions,
    ) {
        if (!allowFramedSoftphone) {
            // blocks the iframe from requesting some permissions by intercepting the call to append
            // the iframe to the specified container and modifies the iframe's `allow` attribute
            // before appending
            // see https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Permissions_Policy
            // oxlint-disable-next-line typescript/unbound-method
            container.appendChild = new Proxy(container.appendChild, {
                apply: (target, thisArgument, [node]: [Node]) => {
                    if (node instanceof HTMLIFrameElement) {
                        node.allow = node.allow
                            .replace(/\bmicrophone\b/, `microphone 'none'`)
                            .replace(/\bcamera\b/, `camera 'none'`);
                    }
                    return Reflect.apply(target, thisArgument, [node]);
                },
            });
        }

        const attempt = Number(localStorage.getItem(attemptKey));
        localStorage.setItem(attemptKey, `${attempt + 1}`);

        const isOverRetryLimit = attempt > retries;

        const loginPopupOverride = loginPopup || isOverRetryLimit;

        let chatSdk: AmazonConnectChatSDK | undefined;
        let chatSpecificCCPOptions = {};

        // Initialize chat SDK and CCP options if chat dependencies provided.
        // Region is required for ChatJS API calls.
        // ChatJS library is eagerly loaded and configured during SDK creation
        if (chatDependencies) {
            const { chatEventCallbacks, chatMessagesCache } = chatDependencies;
            chatSdk = await AmazonConnectChatSDK.create(chatEventCallbacks, chatMessagesCache, connectRegion);
            chatSpecificCCPOptions = { region: connectRegion, chat: { disableRingtone: true } };
        }

        //#region Leverage exponential backoff for timeouts on init retries
        const ccpLoadTimeout = 5_000 * factor ** attempt;
        //#endregion

        connect.core.initCCP(container, {
            ccpUrl,
            secondaryCCPUrl,
            enableGlobalResiliency,
            loginUrl,
            loginPopup: loginPopupOverride,
            loginPopupAutoClose,
            softphone: {
                allowFramedSoftphone,
                VDIPlatform: vdiPlatform,
            },
            ccpLoadTimeout,
            ...chatSpecificCCPOptions,
        });
        // have to happen after initCCP
        withToMetrics(metricsHandler, attempt);
        connect.core.onInitialized(() => localStorage.removeItem(attemptKey));

        if (!loginPopupOverride && loginUrl) {
            const eventBus = connect.core.getEventBus();
            eventBus.subscribe(connect.EventType.ACK_TIMEOUT, () => {
                onAuthRedirect?.();
                globalThis.location.href = loginUrl;
            });
        }

        const { resolve: onReady } = Promise.withResolvers<boolean>();

        // The CCP handshake below (connect.agent) resolves only once the CCP iframe is up and the
        // agent is authenticated. If the iframe never establishes it never resolves or rejects, so
        // this breadcrumb is the last signal before an indefinite hang — absence of the matching
        // "Amazon Connect agent loaded" log means we stalled here.
        AmazonConnectAgentSDK.logger.info(
            { flow: LogFlow.WorkspaceLoading, ccpUrl, attempt, ccpLoadTimeout, loginPopup: loginPopupOverride },
            "Waiting for Amazon Connect agent (CCP handshake)",
        );

        const agent = await new Promise<connect.Agent>(connect.agent);
        AmazonConnectAgentSDK.logger.info(
            { flow: LogFlow.WorkspaceLoading, agentName: agent.getName(), agentArn: agent.getAgentARN() },
            "Amazon Connect agent loaded",
        );

        const {
            features: { transferListEnabled: isTransferListEnabled },
        } = getAppConfigSingleton().getPartnersConfig(partner) ?? {};

        const { queueEndpoints: agentDefaultEndpointMaps } = await fetchEndpointMaps(
            agent,
            partner,
            AmazonConnectAgentSDK.logger,
            isTransferListEnabled,
        );

        // Chat build owns only chat contacts; drop any voice/callback contacts present at load
        // (e.g. a refresh mid-call) before they become tasks. See shouldIgnoreContact.
        const allContacts = agent.getContacts();
        // Gate the ignore behavior on the kill-switch flag (#6973); when off, voice contacts are
        // processed exactly as before. `shouldIgnoreContact` keeps the env.channel==="chat" scoping.
        const shouldIgnore = (contact: connect.Contact) =>
            isNonChatContactIgnoreEnabled && shouldIgnoreContact(contact);
        const contacts = allContacts.filter((contact) => !shouldIgnore(contact));
        const ignoredContacts = allContacts.filter(shouldIgnore);
        if (ignoredContacts.length > 0) {
            AmazonConnectAgentSDK.logger.info(
                { ignored: ignoredContacts.length, channel: env.channel },
                "Ignoring contacts during init",
            );
        }

        const mappedContacts = contacts.map((contact) => ({
            contactId: contact.getContactId(),
            contactType: contact.getType(),
            contactState: contact.getState(),
        }));
        AmazonConnectAgentSDK.logger.debug(
            {
                contacts: mappedContacts,
            },
            "Contacts retrieved",
        );

        const {
            features: { cleanMissedCalls: isCleanMissedContactsEnabled },
        } = getAppConfigSingleton().getPartnersConfig(partner) ?? {};

        const { contactsToClear, filteredContacts } = isCleanMissedContactsEnabled
            ? getFilteredContacts(contacts)
            : { contactsToClear: [], filteredContacts: contacts };

        if (contactsToClear?.length > 0) {
            try {
                await Promise.all(
                    contactsToClear.map(async (contact) => {
                        try {
                            if (isChatContact(contact)) {
                                await endChatContact(contact, chatSdk, AmazonConnectAgentSDK.logger);
                            } else {
                                await withCallbacks((callbacks) => contact.clear(callbacks));
                            }
                        } catch (error) {
                            AmazonConnectAgentSDK.logger.warn(
                                { contactId: contact.getContactId?.(), error, type: contact.getType() },
                                "Failed to clear contact during initialization",
                            );
                        }
                    }),
                );
            } catch (error) {
                AmazonConnectAgentSDK.logger.warn({ error }, "Non-failing clear of contactsToClear encountered error");
            }
        }

        const tasks = await Promise.all(
            filteredContacts.map((contact) => AmazonConnectAgentSDK.createTask(contact, partner)),
        );
        const tasksIds = tasks.map((task) => task.id);
        AmazonConnectAgentSDK.logger.debug(
            {
                tasks: tasksIds,
                filteredContacts: filteredContacts.map((contact) => contact.getContactId()),
                mappedContacts,
            },
            "Tasks created with connect contacts",
        );
        const sessions = useSessionStore.getState();
        const activeSession = sessions.sessions.find((session) => session.id === sessions.selectedSessionId);

        // Check if the current selected session is active, if not then clear it
        if (
            activeSession &&
            activeSession.tasks?.size > 0 &&
            !tasks.some((task) => {
                const activeSessionTaskIds = Array.from(activeSession.tasks.values()).map((task) => task.id);
                return activeSessionTaskIds.includes(task.config.contact.contactId);
            })
        ) {
            useSessionStore.getState().clearSelectedSession();
            AmazonConnectAgentSDK.logger.debug(
                {
                    activeSession: activeSession.id,
                    sessions: sessions.sessions.map((session) => session.id),
                },
                "Clearing the in active selected session during sdk creation",
            );
        }

        const instanceDomain = new URL(ccpUrl).origin;
        const secondaryInstanceDomain = secondaryCCPUrl && new URL(secondaryCCPUrl).origin;
        const agentSdk = new AmazonConnectAgentSDK(
            onReady,
            container,
            partner,
            employeeNum,
            agent,
            agentDefaultEndpointMaps,
            tasks,
            instanceDomain,
            secondaryInstanceDomain,
            chatSdk,
            autoAcceptMindfulCallbacks,
            isNonChatContactIgnoreEnabled,
        );
        AmazonConnectAgentSDK.logger.debug(
            {
                tasks: tasksIds,
                partner,
                mappedContacts,
            },
            "SDK was created with the following tasks",
        );

        agentSdk.onReady(true);
        return agentSdk;
    }

    readonly #partner;
    #agent;
    #activeQuickConnectConnections: Set<string> = new Set<string>();
    /**
     * Tracks audio disclosure Quick Connect connections so we can emit `disclosure_playback_ended`
     * when they disappear from the contact's connections list on the next refresh. Value is the
     * Quick Connect's name so we can include it in the emitted event payload.
     */
    #activeDisclosureConnections: Map<string, string> = new Map<string, string>();
    /**
     * Tracks contact IDs of outbound callback calls that failed (missed or errored) before connecting.
     * Used in onDestroy to distinguish failed callbacks from successful ones, since by that point
     * task.status is "wrapping" in both cases and cannot be used as a discriminator.
     * Entries are added in isOutboundCallbackFailure and removed (via Set.delete) in onDestroy.
     */
    #failedCallbackContactIds: Set<string> = new Set<string>();
    /**
     * Tracks contact IDs where the agent (not the customer) initiated the end of the call.
     * Used in onEnded to suppress the disconnect chime for intentional agent actions
     * (hangup, wrapup, transfer, survey, leaving a conference). Entries are removed in onDestroy.
     */
    #agentEndedContactIds: Set<string> = new Set<string>();
    #callSdk?: CallSdk;
    #chatSdk?: AmazonConnectChatSDK;
    readonly #container;
    #queueEndpoints: Map<string, connect.Endpoint>;
    private readonly onReady: (isReady: boolean) => void;
    private readonly employeeNum: string | null;
    #previousHoldState: boolean | undefined;
    private readonly instanceDomain: string;
    private readonly secondaryInstanceDomain: string | undefined;
    readonly #autoAcceptMindfulCallbacks: boolean;
    readonly #isNonChatContactIgnoreEnabled: boolean;
    #blockedChatTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        onReady: (isReady: boolean) => void,
        container: HTMLElement,
        partner: string,
        employeeNum: string | null,
        agent: connect.Agent,
        queueEndpoints: Map<string, connect.Endpoint>,
        tasks: AmazonConnectContact[],
        instanceDomain: string,
        secondaryInstanceDomain: string | undefined,
        chatSdk?: AmazonConnectChatSDK,
        autoAcceptMindfulCallbacks = false,
        isNonChatContactIgnoreEnabled = false,
    ) {
        super();
        this.onReady = onReady;
        this.employeeNum = employeeNum;
        this.#autoAcceptMindfulCallbacks = autoAcceptMindfulCallbacks;
        this.#isNonChatContactIgnoreEnabled = isNonChatContactIgnoreEnabled;

        this.#partner = partner;
        this.#agent = agent;
        this.#queueEndpoints = queueEndpoints;
        this.#container = container;
        this.instanceDomain = instanceDomain;
        this.secondaryInstanceDomain = secondaryInstanceDomain;

        this.#chatSdk = chatSdk;

        {
            const fallbackAgentName = `${userCache?.givenName}.${userCache?.familyName}`?.toLowerCase();
            const agentId = agent.getAgentARN();
            const agentName = agent?.getConfiguration()?.username?.toLowerCase() ?? fallbackAgentName;
            const { state: activity, timeStamp } = agent.getAvailabilityState();
            const activityUpdatedAt = timeStamp.valueOf();

            const { tasksToInit, tasksToComplete } = filterTasksForInit(tasks);

            const pendingActivityState = agent.getNextState();
            this.onInitialized(
                agentId,
                agentName,
                activity as AgentActivity,
                activityUpdatedAt,
                tasksToInit,
                this.serviceProvider,
                pendingActivityState?.name as AgentActivity,
            );

            if (tasksToComplete.length > 0) {
                const completionPromises = tasksToComplete.map((task) => this.completeTask(task, TASK_COMPLETE_REASON));
                void Promise.allSettled(completionPromises).then((results) => {
                    results.forEach((result, index) => {
                        if (result.status === "rejected") {
                            AmazonConnectAgentSDK.logger.warn(
                                { taskId: tasksToComplete[index].id, error: result.reason },
                                "Failed to complete expired chat task during init",
                            );
                        }
                    });
                });
            }

            let contactIds: string[] = [];

            tasks.forEach((task) => {
                const { contact } = task.config;

                if (isVoiceTask(task) && task.hasCustomer && task.status === "assigned" && task.agentCallId) {
                    // call is already in progress on refresh, marking it as started
                    this.markConferenceStarted(task);
                    void this.onTaskAccepted(task);
                }

                if (isAmazonConnectChatTask(task)) {
                    contactIds.push(contact.contactId);

                    //#region Defensive checks for chat contacts
                    // TODO: Remove defensive checks once confident filtering catches all cases
                    const stateType = contact.getState().type;

                    switch (stateType) {
                        case connect.ContactStateType.MISSED:
                        case connect.ContactStateType.REJECTED:
                        case connect.ContactStateType.ERROR:
                            AmazonConnectAgentSDK.logger.warn(
                                { contactId: contact.contactId, stateType },
                                "Chat with terminal state escaped filtering - ending contact now",
                            );
                            void endChatContact(contact, this.chatSdk, AmazonConnectAgentSDK.logger);

                            return;
                        case connect.ContactStateType.ENDED:
                            AmazonConnectAgentSDK.logger.warn(
                                { contactId: contact.contactId, stateType },
                                "Rehydrating ENDED chat contact (ACW) - should be cleared via wrapup flow",
                            );
                    }
                    //#endregion

                    void this.chatSdk.initializeConnection(contact, { isRehydratedTask: true });
                    if (task.originalChatId) {
                        void this.chatSdk.updateChatNeglectionCount(task.originalChatId, task.chatId, task.partner);
                    }
                }

                this.subscribeToContactEvents(contact);
            });
            if (contactIds.length > 0) {
                // Rehydrate chat metadata for the contacts
                // This will update the store with the neglection and autopilot data.
                void this.chatSdk.rehydrateChatMetadata(contactIds);
            }
        }

        agent.onStateChange((callback) => {
            AmazonConnectAgentSDK.logger.debug(
                { oldActivity: callback.oldState, newActivity: callback.newState, ...agentStateForLog(agent) },
                "onStateChange | agent state",
            );
            // #6973: show/clear the blocked-chat banner as the agent enters/leaves an error state.
            this.evaluateBlockedChat(callback.newState);
            void getRootDispatcher().dispatchBusinessEvent("AgentActivityChanged", {
                activityFrom: callback.oldState,
                activityTo: callback.newState,
            });
            const { state: activity, timeStamp } = agent.getAvailabilityState();
            this.onActivityChanged(activity as AgentActivity, timeStamp.valueOf());
            const { setPendingActivity } = useAgentStore.getState();
            if (callback.oldState === "Callback Pending") {
                clearCallbackState();
            }
            const pendingActivity = agent.getNextState();
            if (pendingActivity) {
                setPendingActivity(pendingActivity.name as AgentActivity);
            } else {
                setPendingActivity(undefined);
            }
        });

        agent.onEnqueuedNextState(() => {
            AmazonConnectAgentSDK.logger.debug(agentStateForLog(agent), "onEnqueuedNextState | agent state");
            const { name: pendingActivity } = agent.getNextState();
            const { setPendingActivity } = useAgentStore.getState();
            AmazonConnectAgentSDK.logger.info({ pendingActivity }, "onEnqueuedNextState | setPendingActivity");
            setPendingActivity(pendingActivity as AgentActivity);
        });

        // Read the routing profile into the store and subscribe to changes (observability only:
        // no UI action is taken here). Fully self-contained and defensive so it can never break init.
        this.initializeRoutingProfile(agent);
        this.evaluateBlockedChatAtInit();

        connect.contact(async (contact) => {
            if (this.#isNonChatContactIgnoreEnabled && shouldIgnoreContact(contact)) {
                // debug (not info): this is an expected, potentially frequent event for multi-skilled
                // experts; the metric below carries production visibility, so the per-contact log is
                // troubleshooting-only. See exwo-observability (avoid high-frequency info noise).
                AmazonConnectAgentSDK.logger.debug(
                    { contactId: contact.contactId, contactType: contact.getType(), channel: env.channel },
                    "Ignoring contact",
                );
                void sendEventMetric("contact.ignored", { channel: env.channel });
                return;
            }

            AmazonConnectAgentSDK.logger.info(
                {
                    contactId: contact.contactId,
                    contactType: contact.getType(),
                    contactState: contact.getState(),
                },
                "contact detected - creating task",
            );

            const task = await AmazonConnectAgentSDK.createTask(contact, this.#partner);

            // For chat tasks, initialize the ChatJS connection before setting up event subscriptions.
            // Also update the chat metadata session id with the session id from the task.
            if (isAmazonConnectChatTask(task)) {
                // A chat routed through → the agent isn't blocked; clear the banner as a backstop in
                // case the recovery state-change was missed.
                this.clearBlockedChat();
                void this.chatSdk.initializeConnection(contact, { isRehydratedTask: false });
                if (task.originalChatId) {
                    void this.chatSdk.updateChatNeglectionCount(task.originalChatId, task.chatId, task.partner);
                }
                if (task.config.sessionId) {
                    void this.chatSdk.updateChatMetadataSessionId(task.chatId, task.config.sessionId, task.partner);
                }
            }

            // Ensure session store updates, event subscriptions, and auto-accept happen in the correct order
            // Important to prevent race conditions and ensure all state changes are captured

            const setupSubscriptions = () => this.subscribeToContactEvents(contact);
            await this.onTaskCreated(task, setupSubscriptions);
        });

        connect.globalResiliency.onConfigureError((error) => {
            AmazonConnectAgentSDK.logger.fatal({ error }, "Global Resiliency setup error");
        });

        connect.globalResiliency.onFailoverPending(({ nextActiveRegion }) => {
            AmazonConnectAgentSDK.logger.info({ nextActiveRegion }, "Global Resiliency failover pending");
        });

        connect.globalResiliency.onFailoverCompleted(async ({ activeRegion, activeCcpUrl }) => {
            AmazonConnectAgentSDK.logger.info({ activeRegion, activeCcpUrl }, "Global Resiliency failover completed");

            // No-op when chatSdk is not initialized
            this.#chatSdk?.updateRegion(activeRegion);

            // refresh agent activity and pending activity (not synced via ACGR)
            const { state: activity, timeStamp } = agent.getAvailabilityState();
            this.onActivityChanged(activity as AgentActivity, timeStamp.valueOf());
            const pendingActivity = agent.getNextState()?.name as AgentActivity | undefined;
            useAgentStore.getState().setPendingActivity(pendingActivity);

            // refresh contacts for the new region (ARNs are different per region)
            await this.fetchContactsForPartner(partner);
        });
    }

    public override get hasActivitySelfManagement() {
        return true;
    }

    /** True when a non-chat contact is stuck in a terminal state, holding the agent non-routable (#6973). */
    private hasBlockingContact(): boolean {
        return this.#agent
            .getContacts()
            .some((contact) => shouldIgnoreContact(contact) && isTerminalContactState(contact.getState()?.type));
    }

    /**
     * Chat build. Shows the closeable blocked-chat banner when a voice contact is holding the agent
     * non-routable (#6973). Entering an agent error state ({@link connect.AgentErrorStates}) is the
     * trigger; after a short debounce we CONFIRM the block by re-checking for a still-terminal ignored
     * contact. Nonprod observation (2026-08-13) suggested a transient (abandoned outbound →
     * FailedConnectAgent) clears its contact within the debounce while a real miss (MissedCallAgent)
     * does not; the state name alone did not separate them. Confirming on the contact also keeps the
     * banner's "clear the voice contact" guidance matched to something actually stuck. Leaving the
     * error state clears the banner. Read-only.
     */
    private evaluateBlockedChat(stateName: string | undefined): void {
        if (env.channel !== "chat" || !this.#isNonChatContactIgnoreEnabled) return;
        // Guarded so a malformed contact can't throw out of the onStateChange handler and take
        // agent-activity tracking with it — the banner is advisory, the handler is not.
        try {
            if (!isAgentErrorState(stateName)) {
                this.clearBlockedChat();
                return;
            }
            clearTimeout(this.#blockedChatTimer);
            this.#blockedChatTimer = setTimeout(() => {
                try {
                    if (!this.hasBlockingContact()) return;
                    useBlockedChatStore.getState().setBlocked(true);
                    AmazonConnectAgentSDK.logger.warn(
                        { agentState: stateName },
                        "Chat build: a voice contact is holding the agent non-routable — chats blocked until the contact is closed in Amazon Connect",
                    );
                    void sendEventMetric("chat.blocked_by_agent_error", { channel: env.channel });
                } catch (error) {
                    AmazonConnectAgentSDK.logger.warn({ error }, "Failed to confirm blocked-chat state");
                }
            }, BLOCKED_CHAT_DEBOUNCE_MS);
        } catch (error) {
            AmazonConnectAgentSDK.logger.warn({ error }, "Failed to evaluate blocked-chat state");
        }
    }

    /**
     * Chat build. At init, sync the banner to the current block state — an ignored contact already
     * terminal at load. Unconditional so a stale `true` left in the (module-level) store by a prior
     * instance (reconnect/failover) resets when the block is gone. No debounce here: a contact still
     * terminal at page load has already outlived any transient failure, so it's a real block.
     */
    private evaluateBlockedChatAtInit(): void {
        if (env.channel !== "chat" || !this.#isNonChatContactIgnoreEnabled) return;
        // Guarded like initializeRoutingProfile: a malformed contact must not break SDK init.
        try {
            const blocked = this.hasBlockingContact();
            useBlockedChatStore.getState().setBlocked(blocked);
            if (blocked) {
                AmazonConnectAgentSDK.logger.warn(
                    { agentState: "init" },
                    "Chat build: a voice contact is holding the agent non-routable at load — chats blocked until the contact is closed in Amazon Connect",
                );
                void sendEventMetric("chat.blocked_by_agent_error", { channel: env.channel });
            }
        } catch (error) {
            AmazonConnectAgentSDK.logger.warn({ error }, "Failed to evaluate blocked-chat state at init");
        }
    }

    /** Cancel any pending block and hide the blocked-chat banner (recovery, close, or a chat arriving). */
    private clearBlockedChat(): void {
        clearTimeout(this.#blockedChatTimer);
        this.#blockedChatTimer = undefined;
        useBlockedChatStore.getState().setBlocked(false);
    }

    private isOutboundCallbackTask(task: AmazonConnectContact | undefined): task is AmazonConnectVoiceContact {
        return (
            task instanceof AmazonConnectVoiceContact &&
            task?.callDirection === "outbound" &&
            (task?.outboundKind === "CallbackNow" || !!task?.previousTaskId)
        );
    }

    private isOutboundCallbackFailure(task: AmazonConnectContact | undefined, reason: string): boolean {
        if (!this.isOutboundCallbackTask(task)) return false;
        // Record the failure before calling onTaskError so the ID is present if anything
        // downstream synchronously checks it (e.g. onDestroy firing in the same tick).
        this.#failedCallbackContactIds.add(task.id);
        this.onTaskError(task, new Error(reason));
        return true;
    }

    private isAlreadyInWrappingState(task: AmazonConnectContact): boolean {
        const session = useSessionStore.getState().getSessionByTaskId(task.id);
        return task.status === "wrapping" || !!session?.wrappingState;
    }

    private async handleMissedCall(contact: connect.Contact) {
        const task = this.tryGetTaskFromStore(contact.contactId);
        const { contactId } = contact;

        const nonAgentConnections = contact
            .getConnections()
            .filter((connection) => connection.getType() !== connect.ConnectionType.AGENT);
        const isCustomerDisconnected = nonAgentConnections.every(
            (connection) => connection.getStatus().type == connect.ConnectionStateType.DISCONNECTED,
        );

        if (this.isOutboundCallbackFailure(task, "Missed outbound callback contact")) {
            AmazonConnectAgentSDK.logger.warn(
                { contactId, taskId: task?.id },
                "Missed outbound callback contact - triggering task error wrapup path",
            );
            return;
        }

        if (!contact.isInbound() && task instanceof AmazonConnectVoiceContact && task.previousTaskId) return;

        if (task) {
            this.onTaskCancelled(task, "TechnicalIssues");
        } else {
            AmazonConnectAgentSDK.logger.warn({ contactId }, "No task found for contact in handleMissedCall");
            sdkEventBus.emit("technical_issues", true);
        }

        try {
            await withCallbacks((callbacks) => contact.clear(callbacks));
            AmazonConnectAgentSDK.logger.info({ contactId }, "Contact successfully cleared after missed call");
        } catch (error) {
            AmazonConnectAgentSDK.logger.warn({ contactId, error }, "Failed to clear contact after missed call");
        }

        try {
            await this.setAgentActivity("Technical Issues" as AgentActivity);
        } catch (error) {
            AmazonConnectAgentSDK.logger.warn({ error }, "Failed to set agent activity to technical issues");
        }

        AmazonConnectAgentSDK.logger.info({ task: task?.toLog(), isCustomerDisconnected }, "handleMissedCall");
    }

    private async handleMissedChat(contact: connect.Contact) {
        const { contactId } = contact;
        try {
            await endChatContact(contact, this.chatSdk, AmazonConnectAgentSDK.logger);
            AmazonConnectAgentSDK.logger.info({ contactId }, "Chat contact successfully ended after missed chat");
        } catch (error) {
            // TODO @asurion-core/expert-workspace-messaging: show in UI that you can't take calls for up to 30 seconds? and determine if this is recoverable
            AmazonConnectAgentSDK.logger.fatal({ contactId, error }, "Failed to end chat contact after missed chat");
        }
    }

    /**
     * Reads the agent's routing profile into the provider-agnostic store (channel capacity + queues),
     * emits `channel_config_changed`, and re-runs on agent refresh. On a channel mismatch (profile has
     * no capacity for this build's channel) it additionally warns and emits the `channel.mismatch_detected`
     * metric — no modal, and no change to the agent's Connect status/aux. Fully guarded so a malformed
     * profile or failing sink can't break init.
     */
    private initializeRoutingProfile(agent: connect.Agent) {
        let lastChannelConfigSnapshot: string | undefined;

        const updateChannelConfig = () => {
            try {
                const routingProfile = extractRoutingProfileInfo(agent);
                if (!routingProfile) {
                    AmazonConnectAgentSDK.logger.warn("Unable to read routing profile from agent configuration");
                    return;
                }

                const channelCapacity = {
                    voice: routingProfile.channelConcurrencyMap.VOICE ?? 0,
                    chat: routingProfile.channelConcurrencyMap.CHAT ?? 0,
                };
                const assignedQueues = routingProfile.queues.map((queue) => ({ id: queue.queueId, name: queue.name }));

                // Dedupe on derived config, not profile id: an in-place concurrency/queue edit keeps the id.
                const channelConfigSnapshot = JSON.stringify({ channelCapacity, assignedQueues });
                if (lastChannelConfigSnapshot === channelConfigSnapshot) {
                    return;
                }

                AmazonConnectAgentSDK.logger.info(
                    {
                        routingProfileName: routingProfile.name,
                        routingProfileId: routingProfile.routingProfileId,
                        channelCapacity,
                        queueCount: assignedQueues.length,
                    },
                    "Channel configuration updated from routing profile",
                );

                useAgentStore.getState().setChannelConfig(channelCapacity, assignedQueues);
                sdkEventBus.emit("channel_config_changed", { channelCapacity, assignedQueues });
                // Record the snapshot only after a successful write+emit so a transient failure retries.
                lastChannelConfigSnapshot = channelConfigSnapshot;

                // The routing profile has no capacity for this build's channel, so the expert can't
                // receive work on it - an operational issue with a clear recovery (fix the profile),
                // so warn (not error). Also emit an Event counter (-> Mimir) to track frequency.
                if (channelCapacity[env.channel] <= 0) {
                    AmazonConnectAgentSDK.logger.warn(
                        { routingProfileName: routingProfile.name, appChannel: env.channel, channelCapacity },
                        "Routing profile has no capacity for this build's channel",
                    );
                    void sendEventMetric("channel.mismatch_detected", { channel: env.channel });
                }
            } catch (error) {
                AmazonConnectAgentSDK.logger.warn(
                    { error },
                    "Failed to update channel configuration from routing profile",
                );
            }
        };

        try {
            updateChannelConfig();
            agent.onRefresh(() => updateChannelConfig());
        } catch (error) {
            AmazonConnectAgentSDK.logger.warn({ error }, "Failed to initialize routing profile subscription");
        }
    }

    private initCallSdk(webSocket: ExpertWorkspaceWebSocket) {
        this.#callSdk = new EvlCallSdk(this.onVoiceIsReady, webSocket);

        if (!this.#callSdk) {
            throw new Error("Cannot initialize AmazonConnectAgentSDK in EVL mode without a valid EvlCallSdk");
        }
    }
    /**
     * Private getter for the Chat SDK. Ensures that the Chat SDK is initialized before it's accessed.
     * @throws {Error} If the Chat SDK is not initialized.
     */
    private get chatSdk(): AmazonConnectChatSDK {
        if (!this.#chatSdk) {
            throw new Error("Chat SDK is not initialized. This operation is not supported in voice-only mode.");
        }
        return this.#chatSdk;
    }

    public async logout(): Promise<void> {
        // Cancel any pending chat-block timer so it can't fire on the terminated agent (#6973).
        this.clearBlockedChat();
        connect.core.terminate();
        this.#container.remove();

        const logoutDomains = [this.instanceDomain];
        if (this.secondaryInstanceDomain) logoutDomains.push(this.secondaryInstanceDomain);

        // Logs out of CCP in each region by loading the logout URL in a hidden iframe, which clears the session cookie
        // for that domain and prevents the need for the agent to manually log out of each region. This is necessary
        // because connect.core.terminate() does not clear the session cookie, which can lead to issues when trying to
        // log back in or switch regions.
        await Promise.all(
            logoutDomains.map(async (domain) => {
                AmazonConnectAgentSDK.logger.info({ domain }, "Logging out of region via iframe");
                const logoutIframe = document.createElement("iframe");
                logoutIframe.src = `${domain}/logout`;
                logoutIframe.style.display = "none";
                document.body.appendChild(logoutIframe);
                await new Promise<void>((resolve) => (logoutIframe.onload = () => resolve()));
                document.body.removeChild(logoutIframe);
                AmazonConnectAgentSDK.logger.info({ domain }, "Successfully logged out of region via iframe");
            }),
        );
    }

    public override canHandleTaskType(task: Task): task is AmazonConnectContact {
        return task instanceof AmazonConnectContact;
    }

    public override get agentActivities(): AgentActivity[] {
        return this.#agent.getAgentStates().map(({ name }) => name as AgentActivity);
    }

    public override get availableAgentActivity(): AgentActivity {
        const { name } = getAgentStateByType(this.#agent, connect.AgentStateType.ROUTABLE);
        return name as AgentActivity;
    }

    public override get offlineAgentActivity(): AgentActivity {
        const { name } = getAgentStateByType(this.#agent, connect.AgentStateType.OFFLINE);
        return name as AgentActivity;
    }

    public override get defaultAgentActivity(): AgentActivity | undefined {
        if (env.channel === "chat") {
            // always return Offline for chat - chat can have multiple partners per agent.
            return this.offlineAgentActivity;
        }
        const partnerDefaultAgentActivity = getAppConfigSingleton().getPartnersConfig(
            this.#partner,
        )?.defaultAgentActivity;
        return partnerDefaultAgentActivity;
    }

    public override isAgentActivitySelectable(activity: AgentActivity): boolean {
        // FIXME: chat-only carve-out, remove when the "messaging" aux is retired from Connect.
        if (isMessagingAuxBlockedInChat(env.channel, activity)) {
            return false;
        }

        const { type } = getAgentStateByActivity(this.#agent, activity);
        switch (type) {
            case connect.AgentStateType.ROUTABLE:
            case connect.AgentStateType.NOT_ROUTABLE:
                return true;
            case connect.AgentStateType.INIT:
            case connect.AgentStateType.SYSTEM:
            case connect.AgentStateType.ERROR:
                return false;
            case connect.AgentStateType.OFFLINE: {
                return (
                    getAppConfigSingleton().getPartnersConfig(this.#partner).features
                        .isOfflineAgentActivitySelectable ?? false
                );
            }
            default: {
                const unexpectedType: never = type;
                AmazonConnectAgentSDK.logger.warn(
                    { unexpectedType },
                    "received an unexpected/undocumented agent state type",
                );
                return false;
            }
        }
    }

    public override async setAgentActivity(activity: AgentActivity, _activityUpdatedAt?: number): Promise<void> {
        // TODO @asurion-core/expert-workspace-platform: wrap this function in a try/catch
        const state = getAgentStateByActivity(this.#agent, activity);
        const pendingActivityState = this.#agent.getNextState();

        if (pendingActivityState && state.name === pendingActivityState.name) {
            AmazonConnectAgentSDK.logger.info(
                { pendingActivity: pendingActivityState.name },
                "Attempted to enqueue existing pending activity",
            );
            return;
        }

        AmazonConnectAgentSDK.logger.info({ activity }, "Setting agent activity");
        await withCallbacks((callbacks) => this.#agent.setState(state, callbacks, { enqueueNextState: true }), {
            logger: AmazonConnectAgentSDK.logger,
            action: "agent.setState",
            logContext: {
                ...agentStateForLog(this.#agent),
                activityToSet: activity,
            },
        });
    }

    public override getAgentActivity(): AgentActivity {
        const { state: activity } = this.#agent.getAvailabilityState();
        return activity as AgentActivity;
    }

    public override async updateAgentAttributes(_attributes: Record<MutableAgentAttribute, JsonValue>): Promise<void> {
        await Promise.resolve();
        AmazonConnectAgentSDK.logger.info("Method not implemented: updateAgentAttributes");
    }

    public async fetchContactsForPartner(partner: Partner): Promise<void> {
        /**
         * Pulls Quick Connects from an agent's routing profile via the active Contact
         * and stores them as connect.Endpoints to interact with.
         * @see https://docs.aws.amazon.com/connect/latest/APIReference/API_Endpoint.html
         *
         * We protect the network call in fetchEndpointMaps with the transferListEnabled
         * feature flag.
         */
        const {
            features: { transferListEnabled: isTransferListEnabled },
        } = getAppConfigSingleton().getPartnersConfig(partner) ?? {};

        const { queueEndpoints } = await fetchEndpointMaps(
            this.#agent,
            partner,
            AmazonConnectAgentSDK.logger,
            isTransferListEnabled,
        );

        this.#queueEndpoints = queueEndpoints;
    }

    public override getPartners(): ReadonlySet<Partner> {
        return new Set<Partner>().add(this.#partner);
    }

    public override async acceptTask(task: AmazonConnectContact): Promise<void> {
        if (!isAmazonConnectChatTask(task)) {
            const { softphoneAutoAccept } = this.#agent.getConfiguration();
            if (softphoneAutoAccept) return;
        }

        try {
            await withConnectMetrics(
                "acceptContact",
                () => withCallbacks((callbacks) => task.config.contact.accept(callbacks)),
                {
                    logger: AmazonConnectAgentSDK.logger,
                    rethrow: true,
                    logResult: false,
                    failureLogLevel: "warn",
                },
            );
            AmazonConnectAgentSDK.logger.info(
                {
                    contactId: task.config.contact.contactId,
                    contactType: task.config.type,
                },
                "Contact successfully accepted",
            );
        } catch (error) {
            AmazonConnectAgentSDK.logger.error(
                {
                    error,
                    contactId: task.config.contact.contactId,
                    contactType: task.config.type,
                },
                "Failed to accept contact",
            );
        }
    }

    public override async rejectTask(task: AmazonConnectContact): Promise<void> {
        await withConnectMetrics(
            "rejectTask",
            () => withCallbacks((callbacks) => task.config.contact.reject(callbacks)),
            {
                logger: AmazonConnectAgentSDK.logger,
                rethrow: true,
                logResult: false,
                failureLogLevel: "warn",
            },
        );
    }

    public override async wrapupTask(task: AmazonConnectContact): Promise<void> {
        this.#agentEndedContactIds.add(task.id);
        const contact = task.config.contact;
        const handleWrapupTask = () => {
            // destroy is deprecated for chat contacts, destroying properly from the agent connection
            if (isChatContact(contact)) {
                if (isAmazonConnectChatTask(task)) {
                    void this.chatSdk?.updateChatCloseReason(task.chatId, "ClosedByExpert", task.partner);
                }
                const agentConnection = contact.getAgentConnection();
                if (agentConnection) {
                    return withCallbacks((callbacks) => agentConnection.destroy(callbacks));
                }
                AmazonConnectAgentSDK.logger.warn(
                    { contactId: contact.contactId },
                    "No agent connection available when attempting to wrap up chat contact, performing onTaskWrapping anyway",
                );
                // Perform the standard wrapping/cleanup flow for the task rather than silently succeeding
                return this.onTaskWrapping(task);
            }

            return withCallbacks((callbacks) => task.config.contact.destroy(callbacks));
        };

        await withConnectMetrics("wrapupTask", handleWrapupTask, {
            logger: AmazonConnectAgentSDK.logger,
            rethrow: true,
            logResult: false,
            failureLogLevel: "warn",
        });
    }

    public override async completeTask(task: AmazonConnectContact, reason: TaskCompletedReason): Promise<void> {
        const contact = task.config.contact;
        const completeTaskLogger = AmazonConnectAgentSDK.logger.child({
            taskId: task.id,
            contactId: contact.contactId,
            contactType: contact.getType(),
            status: contact.getState().type,
        });

        if (reason === "CallbackInitiated") {
            contactToTaskCompletedReason.set(contact, reason);
        }

        completeTaskLogger.info(
            `Completing task. Calling contact.clear() on contact in state: ${contact.getState().type}`,
        );

        await withConnectMetrics(
            "completeTask",
            async () => {
                if (isChatContact(contact)) {
                    await endChatContact(contact, this.chatSdk, AmazonConnectAgentSDK.logger);
                } else {
                    // all other contacts including voice
                    await withCallbacks((callbacks) => contact.clear(callbacks));
                }
            },
            {
                logger: completeTaskLogger,
                rethrow: true,
                logResult: false,
                failureLogLevel: "warn",
            },
        );
    }

    public override cancelTask(_task: AmazonConnectContact): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public async fetchContactsForTask(task: AmazonConnectContact): Promise<void> {
        /**
         * Pulls Quick Connects from an agent's routing profile via the active Contact
         * and stores them as connect.Endpoints to interact with.
         * @see https://docs.aws.amazon.com/connect/latest/APIReference/API_Endpoint.html
         *
         * We protect the network call in fetchEndpointMaps with the transferListEnabled
         * feature flag.
         */
        const {
            features: { transferListEnabled: isTransferListEnabled },
        } = getAppConfigSingleton().getPartnersConfig(task.partner) ?? {};

        const { queueEndpoints } = await fetchEndpointMaps(
            this.#agent,
            task.partner,
            AmazonConnectAgentSDK.logger,
            isTransferListEnabled,
        );

        this.#queueEndpoints = queueEndpoints;
    }

    public override async startConferenceWithEndpointArn(
        task: AmazonConnectContact,
        endpointARN: string,
    ): Promise<void> {
        assertVoiceTask(task, "Only voice tasks can start a conference");
        const conferenceLogger = AmazonConnectAgentSDK.logger.child({
            action: "startConferenceWithEndpointArn",
            taskId: task.id,
            contactId: task.config.contact.contactId,
            endpointARN,
        });

        if (!task.hasCustomer) {
            conferenceLogger.debug("no customer connection; skipping conference");
            return;
        }

        // Guard the addConnection contact-state precondition. `addConnection` rejects with
        // InvalidStateException ("Invalid agent or contact state") unless the contact is connected and
        // the customer leg is live (CONNECTED — which also excludes on hold / connecting / disconnected,
        // since a held connection reports ConnectionStateType.HOLD). Fail fast with a typed error so the
        // caller degrades appropriately instead of surfacing a confusing AWS 400. (`task.hasCustomer` is
        // hardcoded true for voice, so it does NOT check live state — these checks do.) The two failure
        // modes are thrown as distinct typed errors so callers can tell a connectivity problem apart
        // from a concurrency one (metric reason + agent-facing hint differ). Uses `getState().type` to
        // match how the rest of this SDK reads contact/connection state.
        const { contact } = task.config;
        const customerConnection = contact.getInitialConnection();
        const contactConnected =
            contact.getState().type === connect.ContactStateType.CONNECTED &&
            customerConnection?.getState()?.type === connect.ConnectionStateType.CONNECTED;
        if (!contactConnected) {
            conferenceLogger.warn(
                {
                    contactState: contact.getState().type,
                    customerConnectionState: customerConnection?.getState()?.type ?? "none",
                },
                "contact not connected; skipping disclosure addConnection",
            );
            throw new DisclosureContactNotReadyError();
        }
        if (this.#activeDisclosureConnections.size !== 0) {
            conferenceLogger.warn(
                { activeDisclosureConnections: this.#activeDisclosureConnections.size },
                "disclosure already in flight; skipping addConnection",
            );
            throw new DisclosureInFlightError();
        }

        // Build the endpoint straight from the ARN instead of discovering it via getEndpoints, so the
        // conference does not depend on the target being in the agent's routing profile.
        const endpoint = buildEndpointFromArn(endpointARN);

        // Adding a participant always puts everyone on hold in Amazon Connect. Capture the state BEFORE
        // addConnection so the auto-resume logic in subscribeToContactEvents behaves the same as it
        // does for playAudioInConference.
        this.#previousHoldState = (task as AmazonConnectVoiceContact).holdState.onHold;

        conferenceLogger.info("dispatching conference to endpoint ARN");
        await withConnectMetrics(
            "startConferenceWithEndpointArn",
            () => withCallbacks((callbacks) => task.config.contact.addConnection(endpoint, callbacks)),
            { logger: conferenceLogger, rethrow: true, logResult: false, failureLogLevel: "warn" },
        );
    }

    public override async hangupCall(task: AmazonConnectContact): Promise<void> {
        this.#agentEndedContactIds.add(task.id);
        // todo: this should be modeled differently to not need to assert
        assertVoiceTask(task, "Only voice tasks can be hung up");
        const { contact } = task.config;
        const activeInitialConnection = contact.getActiveInitialConnection();
        if (activeInitialConnection === null) return;
        await withCallbacks((callbacks) => activeInitialConnection.destroy(callbacks));
    }

    public override areSurveysAutomatic(): boolean {
        return this.getPartnerConfig().features.surveyEnabled;
    }

    public override async sendToSurvey(task: AmazonConnectContact, surveyMeta?: SurveyMeta): Promise<void> {
        this.#agentEndedContactIds.add(task.id);
        assertVoiceTask(task, "Only voice tasks can be transferred to survey");

        const payload: SurveyRequest = {
            customerCaseId: task.id,
            customerIdKind: "mdn",
            customerId: task.mdn,
            empId: this.employeeNum ?? "N/A",
            adName: this.#agent.getConfiguration().username,
            ...(surveyMeta?.resolution !== undefined && { resolution: surveyMeta.resolution }),
            ...(surveyMeta?.claimFiled !== undefined && { claimFiled: surveyMeta.claimFiled }),
        };

        await sendSurveyData(task.id, payload);
        await withCallbacks((callbacks) => task.config.contact.getAgentConnection().destroy(callbacks));
    }

    public override async holdCall(task: AmazonConnectContact): Promise<void> {
        // todo: this should be modeled differently to not need to assert
        assertVoiceTask(task, "Only voice tasks can be put on hold");
        const { contact } = task.config;
        const initialConnection = contact.getInitialConnection();
        if (initialConnection.isOnHold()) return;
        await withCallbacks((callbacks) => initialConnection.hold(callbacks));
        await onHoldState(contact, initialConnection, true);
        this.onHoldCall(task);
    }

    public override async resumeCall(task: AmazonConnectContact): Promise<void> {
        try {
            // todo: this should be modeled differently to not need to assert
            assertVoiceTask(task, "Only voice tasks can be resumed");
            const { contact } = task.config;
            const initialConnection = contact.getInitialConnection();
            if (!initialConnection.isOnHold()) return;
            await withCallbacks((callbacks) => initialConnection.resume(callbacks));
            await onHoldState(contact, initialConnection, false);
            this.onResumeCall(task);
        } catch (error) {
            // @asurion-core/expert-workspace-platform show error in UI
            AmazonConnectAgentSDK.logger.warn({ error }, "Failed to resume call with error:");
        }
    }

    public override async muteCall(task: AmazonConnectContact, shouldMute: boolean): Promise<void> {
        try {
            // todo: this should be modeled differently to not need to assert
            assertVoiceTask(task, "Only voice tasks can be muted");

            const { contact } = task.config;

            //#region adapted from https://github.com/amazon-connect/amazon-connect-streams/blob/8cfe111ef0a850b84cac35450324b93f36dcbc41/cheat-sheet.md#mute-agent
            const activeConnections = contact.getActiveConnections();
            if (activeConnections.length === 0) {
                throw new Error("No Active Connections to mute");
            }
            if (contact.isMultiPartyConferenceEnabled() && activeConnections.length > 2) {
                const { length: connectingCount } = contact
                    .getConnections()
                    .filter((connections) => connections.isConnecting());
                if (connectingCount > 0) {
                    throw new Error("Agent Connection cannot be muted while multi party participant is connecting");
                }
                const agentConnection = contact.getAgentConnection() as connect.VoiceConnection;
                if (shouldMute) agentConnection.muteParticipant();
                else agentConnection.unmuteParticipant();
            } else {
                AmazonConnectAgentSDK.logger.debug("Agent connection muted at the client side");
                if (shouldMute) this.#agent.mute();
                else this.#agent.unmute();
            }
            //#endregion

            this.onMuteCall(task, shouldMute);
        } catch (error) {
            AmazonConnectAgentSDK.logger.warn({ error }, "Failed to mute call with error:");
        }

        return Promise.resolve();
    }

    public override async sendDtmfDigits(digits: string): Promise<void> {
        const { 0: contact, length: contactsCount } = this.#agent.getContacts(connect.ContactType.VOICE);
        if (contactsCount === 0) throw new Error("No contact available");
        if (contactsCount > 1) throw new Error("More than one contact is available");
        const agentConnection = contact.getAgentConnection();
        await Promise.all([
            withCallbacks((callbacks) => agentConnection.sendDigits(digits, callbacks)),
            playDigits(digits),
        ]);
    }

    public override async redirectCallToTFN(task: AmazonConnectContact, redirectTo: string): Promise<void> {
        await withCallbacks((callbacks) =>
            task.config.contact.addConnection(connect.Endpoint.byPhoneNumber(redirectTo), callbacks),
        );
        await this.leaveConference(task);
    }

    public override async redirectCallToSIP(task: AmazonConnectContact, redirectTo: string): Promise<void> {
        const endpoint = this.#queueEndpoints.get(redirectTo.trim());

        if (!endpoint) {
            AmazonConnectAgentSDK.logger.error(
                { redirectTo },
                "redirectCallToSIP | No matching Quick Connect endpoint found",
            );
            throw new Error(`No Quick Connect endpoint found for: ${redirectTo}`);
        }

        await withCallbacks((callbacks) => task.config.contact.addConnection(endpoint, callbacks));

        await this.leaveConference(task);
    }

    public override async addConferenceParticipant(
        task: AmazonConnectContact,
        _name: string,
        to: string,
        isSip?: boolean,
    ): Promise<void> {
        this.#previousHoldState = (task as AmazonConnectVoiceContact).holdState.onHold;

        const getEndpoint = (): connect.Endpoint => {
            try {
                if (isSip) {
                    const matchingEndpoint = this.#queueEndpoints.get(to.trim());
                    if (matchingEndpoint) return matchingEndpoint;
                }
            } catch (error) {
                throw new Error(`Failed to resolve endpoint: ${String(error)}`);
            }

            return connect.Endpoint.byPhoneNumber(to);
        };

        const endpoint = getEndpoint();
        await withCallbacks((callbacks) => task.config.contact.addParticipant(endpoint, callbacks));
    }

    public override async updateParticipant(
        { config: { contact } }: AmazonConnectContact,
        participantCallId: string,
        { hold, endConferenceOnExit }: UpdateParticipantOptions,
    ): Promise<void> {
        if (endConferenceOnExit) throw new Error("endConferenceOnExit not yet supported");
        if (hold === undefined) return;

        const connections = contact
            .getActiveConnections()
            .filter((connection) => connection instanceof connect.VoiceConnection)
            .filter((connection) => connection.connectionId === participantCallId);

        if (connections.length === 0) throw new Error("no active connection found for participant");
        if (connections.length > 1) throw new Error("more than one active connection found for participant");
        const [connection] = connections;
        if (connection.isOnHold() === hold) return;
        await withCallbacks((callbacks) => connection[hold ? "hold" : "resume"](callbacks));
        await onHoldState(contact, connection, hold);
    }

    public override async removeParticipant(task: AmazonConnectContact, callId: string): Promise<void> {
        const connection = task.config.contact.getConnections().find(({ connectionId }) => connectionId === callId);
        if (!connection) return;
        await withCallbacks((callbacks) => connection.destroy(callbacks));
    }

    public override async removePendingParticipant(task: AmazonConnectContact, callId: string): Promise<void> {
        await this.removeParticipant(task, callId);
    }

    public override async leaveConference(task: AmazonConnectContact): Promise<void> {
        this.#agentEndedContactIds.add(task.id);
        await withCallbacks((callbacks) => task.config.contact.getAgentConnection().destroy(callbacks));
    }

    public override async endConference(task: AmazonConnectContact): Promise<void> {
        this.#agentEndedContactIds.add(task.id);
        await Promise.all(
            task.config.contact
                .getActiveConnections()
                .map((connection) => withCallbacks((callbacks) => connection.destroy(callbacks))),
        );
    }

    public override async resetConferenceState(_task: AmazonConnectContact): Promise<void> {
        // Do nothing
    }

    protected override async createCustomerCallbackForTask(callbackState: CallbackState): Promise<string> {
        const relatedContactId = callbackState.sessionId;
        const contactId = await this.createOutboundCall(callbackState.callbackMDN, relatedContactId);
        return contactId;
    }

    protected override async createOutboundCall(callbackMdn: string, relatedContactId?: string): Promise<string> {
        AmazonConnectAgentSDK.logger.info("Creating outbound call");
        try {
            await withConnectMetrics(
                "createOutboundCall",
                () => {
                    return withCallbacks(
                        (callbacks) => {
                            this.#agent.connect(connect.Endpoint.byPhoneNumber(callbackMdn), {
                                ...callbacks,
                                relatedContactId,
                            });
                        },
                        { timeout: 20_000 },
                    );
                },
                {
                    logger: AmazonConnectAgentSDK.logger,
                    rethrow: true,
                    logResult: false,
                    failureLogLevel: "warn",
                },
            );
        } catch (error) {
            this.onTaskError(undefined, error);
            throw error;
        }
        AmazonConnectAgentSDK.logger.info({ relatedContactId }, "Connected agent to callBackMdn");

        const contact = await new Promise<connect.Contact>((resolve) => {
            const subscription = connect.contact((contact) => {
                AmazonConnectAgentSDK.logger.info({ contact }, "Getting subscription for contact");
                const phoneNumber = contact.getInitialConnection().getEndpoint().phoneNumber;
                if (phoneNumber !== callbackMdn) {
                    AmazonConnectAgentSDK.logger.warn(
                        "Contact phone number does not match MDN. Unable to resolve contact and unsubscribe",
                    );
                    return;
                }
                AmazonConnectAgentSDK.logger.info("Resolving contact and unsubscribing");
                resolve(contact);
                subscription.unsubscribe();
            });
        });
        return contact.contactId;
    }

    private static async createTask(contact: connect.Contact, defaultPartner: string): Promise<AmazonConnectContact> {
        const taskConfig = getAmazonContactConfig(contact);
        const taskName = AmazonConnectContact.getContactName(taskConfig);
        const taskPartner = contact.getAttributes()?.PARTNER?.value;

        if (!taskPartner) {
            AmazonConnectAgentSDK.logger.warn(
                { contact: contact?.contactId },
                "No partner found in contact attributes",
            );
        }

        if (taskConfig.type === "voice") {
            const channelShareData = await getCallDetails(taskConfig.sessionId, defaultPartner);
            const client = channelShareData?.CLIENT;
            const partner = channelShareData?.PARTNER;

            // TODO: Remove normalizePartner and logs once we verify that lowercased partner from CSD is working as expected
            // Use `||` (not `??`) so a present-but-empty Connect PARTNER attribute ("") falls through to the
            // CSD PARTNER/CLIENT signals rather than short-circuiting to "". `?? defaultPartner` then backstops
            // the fully-absent case with the expert's own partner (Verizon voice empty-PARTNER incident 2026-08-05).
            const resolvedFromSignal = normalizePartner(taskPartner || partner || client);
            const normalizedPartner = resolvedFromSignal ?? defaultPartner;

            // Emit a filterable warn whenever the empty-PARTNER fallback engages, so we can watch the
            // fix recover sessions in prod (each occurrence would previously have sent partner:"" -> 400).
            // Remove alongside the diagnostic info log once Connect stops emitting empty PARTNER.
            if (!taskPartner?.trim()) {
                AmazonConnectAgentSDK.logger
                    .child({
                        taskId: contact.contactId,
                        action: "createTask",
                    })
                    .warn(
                        {
                            csdPartner: partner ?? "undefined",
                            csdClient: client ?? "undefined",
                            taskPartner: taskPartner ?? "undefined",
                            normalizedPartner,
                            resolvedVia: resolvedFromSignal ? (partner ? "csdPartner" : "csdClient") : "defaultPartner",
                        },
                        "Connect PARTNER attribute empty; resolved partner via fallback",
                    );
            }

            if (taskPartner !== normalizedPartner || !taskPartner || !partner || !client) {
                AmazonConnectAgentSDK.logger
                    .child({
                        taskId: contact.contactId,
                        action: "createTask",
                    })
                    .info(
                        {
                            csdPartner: partner ?? "undefined",
                            csdClient: client ?? "undefined",
                            taskPartner: taskPartner ?? "undefined",
                            normalizedPartner: normalizedPartner ?? "undefined",
                        },
                        `CSD partner does not match normalized partner`,
                    );
            }

            const language = channelShareData?.LANGUAGE;
            const locale = language && isLocale(language) ? language : undefined;
            const mdn = channelShareData?.mdn ?? contact.getAttributes()?.mdn?.value;
            const callerId = channelShareData?.callerId;
            return new AmazonConnectVoiceContact(
                taskName,
                taskConfig,
                mdn,
                callerId,
                locale,
                normalizedPartner,
                client,
            );
        }
        if (taskConfig.type === "chat") {
            //! Do not use defaultPartner for chat tasks! Agents can support multiple partners in their routing profile!
            AmazonConnectAgentSDK.logger.debug(
                { contactId: contact.contactId, contactAttributes: contact.getAttributes() },
                "Creating chat task",
            );

            return new AmazonConnectChatContact(taskName, taskConfig);
        }

        throw new Error(`task type not supported: ${taskConfig.type}`);
    }

    /**
     * Subscribes to Amazon Connect contact events
     * Handles lifecycle, state, and business logic updates for contacts.
     *
     * @param contact - The Amazon Connect contact to subscribe to events for
     */
    private subscribeToContactEvents(contact: connect.Contact) {
        const taskLogger = AmazonConnectAgentSDK.logger.child({
            taskId: contact.contactId,
            action: "subscribeToContactEvents",
        });

        this.#activeQuickConnectConnections.clear();
        this.#activeDisclosureConnections.clear();

        contact.onRefresh(() => {
            try {
                taskLogger.debug("Contact refresh event");
                const task = this.getTaskFromStore(contact.contactId);

                if (isAmazonConnectChatTask(task)) {
                    const newStatus = task.calculateStatusFromContact();
                    if (task.status === "assigned" && newStatus === "pending") {
                        taskLogger.warn(
                            { currentStatus: task.status, skippedNewStatus: newStatus },
                            "Skipping status update - preventing downgrade from refresh event edge case",
                        );
                    } else {
                        useSessionStore.getState().updateTaskStatus(task.id, newStatus);
                    }
                }

                if (isVoiceTask(task)) {
                    // Recover from a missed one-shot connect/accept event (e.g. a mid-call Voice SDK
                    // re-init): onRefresh fires repeatedly, so once CCP reports the contact CONNECTED,
                    // re-derive the state those events normally set. Status recovery is retried on every
                    // refresh (guarded only by status) so it can't get half-applied; conferenceStarted is
                    // idempotent via its own guard. Without recovery the dial pad never mounts and the
                    // expert can't press 1 on a mindful call.
                    const isContactConnected = contact.getState().type === connect.ContactStateType.CONNECTED;
                    if (isContactConnected && task.agentCallId && task.status === "pending") {
                        // onAccepted/onConnected missed — recover the pending->assigned transition, else the
                        // pending bar renders nothing. Scoped to "pending" (the only non-assigned,
                        // non-terminal status) so a wrapping/ended task is never forced back to assigned.
                        void withConnectMetrics("onTaskAccepted", () => this.onTaskAccepted(task), {
                            logger: taskLogger,
                            rethrow: false,
                            logResult: false,
                            failureLogLevel: "warn",
                        });
                    }
                    if (isContactConnected && task.agentCallId && !task.conferenceStarted) {
                        taskLogger.warn(
                            { contactId: contact.contactId },
                            "Reconciling conference started from contact refresh (missed onConnected)",
                        );
                        this.markConferenceStarted(task);
                        // TODO(#6870): dedupe call_accepted by a re-init-stable id. A mid-call Voice SDK
                        // re-init rebuilds the task with conferenceStarted=false, so this (and onConnected)
                        // can replay the whisper tone / re-bootstrap the Gaia session context.
                        this.onCallAccepted(task);
                    }

                    // TODO(#6869): voice onRefresh does not reconcile the terminal ENDED->wrapping transition
                    // the way chat does, so a missed onEnded/onDestroy can leave a dead call's controls mounted.
                    // Mirror this connect-side recovery on the teardown side.

                    const agentConnection = contact.getAgentConnection() as connect.VoiceConnection;
                    if (task.isMuted !== agentConnection.isMute()) {
                        this.onMuteCall(task, agentConnection.isMute());
                    }

                    const initialConnection = contact.getInitialConnection();
                    const isOnHold = initialConnection.isOnHold();
                    if (task.holdState.onHold !== isOnHold) {
                        if (isOnHold) this.onHoldCall(task);
                        else this.onResumeCall(task);
                    }

                    const taskId = contact.contactId;
                    const participants = contactToParticipants(contact);
                    // Look for any new Quick Connects that were just added. If a new one was added, resume the call if it was previously not on hold
                    // since AWS Connect automatically puts everyone on hold when adding a participant to a conference and there's no way to circumvent this behavior.
                    // See: https://repost.aws/questions/QUR1M_gowvQo-ITOhSnkKuyQ/add-party-to-call-without-putting-other-parties-on-hold-amazon-connect
                    const quickConnectConnections = contactToQuickConnectConnections(contact);
                    // Map of currently-known Quick Connect connections by ID so we can check both
                    // presence AND active state when detecting end-of-playback (Connect may keep
                    // a disconnected connection in the list with state=DISCONNECTED).
                    const currentQuickConnectMap = new Map(quickConnectConnections.map((c) => [c.connectionId, c]));

                    for (const conn of quickConnectConnections) {
                        const { connectionId } = conn;
                        if (this.#activeQuickConnectConnections.has(connectionId)) continue;

                        this.#activeQuickConnectConnections.add(connectionId);

                        // Restore hold state if needed — runs for any Quick Connect since adding any
                        // participant puts the customer on hold.
                        if (this.#previousHoldState !== undefined && !this.#previousHoldState) {
                            void this.resumeCall(task);
                        }
                        this.#previousHoldState = undefined;

                        // Emit disclosure events only for audio Quick Connects so regular transfers
                        // don't pollute the disclosure signal.
                        // Match on the live connection's QC name so detection does not depend on the
                        // getEndpoints-sourced #audioQuickConnectEndpoints map (which is empty when the
                        // QC isn't in the agent's routing profile — e.g. the ARN-based conference path).
                        const quickConnectName = conn.getQuickConnectName() ?? "";
                        if (!quickConnectName.includes("PreRecordedDisclosures")) continue;

                        // Only an ACTIVE connection is a genuine start. Connect keeps a disconnected
                        // disclosure connection in the list (state=DISCONNECTED) for a window after
                        // playback ends; because the ended-loop below clears connectionId from
                        // #activeQuickConnectConnections, without this guard the started-loop would
                        // re-detect that lingering inactive connection on the next refresh and
                        // flip-flop started/ended repeatedly.
                        if (!conn.isActive()) continue;

                        this.#activeDisclosureConnections.set(connectionId, quickConnectName);
                        taskLogger.info(
                            { contactId: contact.contactId, connectionId, quickConnectName },
                            "disclosure_playback_started",
                        );
                        sdkEventBus.emit("disclosure_playback_started", {
                            contactId: contact.contactId,
                            connectionId,
                            quickConnectName,
                        });
                    }

                    // VoiceConnection has no onDisconnect event subscription, so detect ended
                    // disclosure playbacks during this refresh. A connection is considered ended
                    // when it's either gone from the list OR still present but no longer active
                    // (Connect keeps DISCONNECTED connections in the list for a window).
                    for (const [connectionId, quickConnectName] of this.#activeDisclosureConnections) {
                        const conn = currentQuickConnectMap.get(connectionId);
                        const isEnded = !conn || !conn.isActive();
                        if (!isEnded) continue;
                        this.#activeDisclosureConnections.delete(connectionId);
                        this.#activeQuickConnectConnections.delete(connectionId);
                        taskLogger.info(
                            {
                                contactId: contact.contactId,
                                connectionId,
                                quickConnectName,
                                reason: conn ? "inactive" : "removed",
                            },
                            "disclosure_playback_ended",
                        );
                        sdkEventBus.emit("disclosure_playback_ended", {
                            contactId: contact.contactId,
                            connectionId,
                            quickConnectName,
                        });
                    }

                    if (hasCustomerLeft(contact)) useSessionStore.getState().setCustomerLeftTheCall(taskId);

                    const prevParticipantCallIds = getParticipantCallIds(task.conferenceParticipants);
                    const participantCallIds = getParticipantCallIds(participants);
                    const missingCallIds = difference([...prevParticipantCallIds], [...participantCallIds]);

                    for (const missingCallId of missingCallIds) {
                        const participant = task.conferenceParticipants.find(
                            (p) => p.participantCallId === missingCallId,
                        );

                        if (participant) {
                            if (participant.status === "in-progress") {
                                taskLogger.info(`Participant ${missingCallId} is still in-progress. Skipping removal.`);
                                continue;
                            }
                        }

                        taskLogger.info(`Refresh occurred. Removing missing participant: ${missingCallId}`);
                        useSessionStore.getState().removeConferenceParticipant(taskId, missingCallId);
                    }

                    for (const participant of participants) {
                        useSessionStore.getState().upsertConferenceParticipant(taskId, participant);
                    }
                }
            } catch (error) {
                taskLogger.warn({ error }, "Failed to refresh task");
            }
        });

        contact.onAccepted(() => {
            taskLogger.debug({ contactState: contact.getState() }, "contact accepted event");

            const task = this.getTaskFromStore(contact.contactId);

            void withConnectMetrics("onTaskAccepted", () => this.onTaskAccepted(task), {
                logger: taskLogger,
                rethrow: true,
                logResult: false,
                failureLogLevel: "error",
            });
        });

        contact.onConnected(() => {
            taskLogger.debug("contact connected event");

            // Auto-acknowledge Mindful callbacks (DTMF "1") on connect, once enabled for this expert.
            if (this.#autoAcceptMindfulCallbacks && isMindfulCallback(contact)) {
                void acceptMindfulCallback(contact, taskLogger);
            }

            const task = this.getTaskFromStore(contact.contactId);
            if (!task) {
                taskLogger.warn({ contactId: contact.contactId }, "No task found for contact in onConnected handler");
                return;
            }
            // Refresh Quick Connect endpoints (incl. audio-disclosure QCs) at the start of each
            // voice call, so the map reflects the agent's current routing profile instead of the
            // login-time snapshot (see AmazonConnectAgentSDK.create), which surfaces as the
            // "audio disclosure endpoint missing" condition. Partner-agnostic — the emitted refresh
            // is filtered by consumers. rethrow:false so a transient getEndpoints failure just warns
            // and leaves the previous map intact.
            if (isVoiceTask(task)) {
                void withConnectMetrics("refreshEndpointsOnConnect", () => this.fetchContactsForPartner(task.partner), {
                    logger: taskLogger,
                    rethrow: false,
                    logResult: false,
                    failureLogLevel: "warn",
                });
            }
            // `contact.onAccepted()` is not triggered for outbound calls and sometimes not for incoming either
            if (task.status !== "assigned") {
                void withConnectMetrics("onTaskAccepted", () => this.onTaskAccepted(task), {
                    logger: taskLogger,
                    rethrow: true,
                    logResult: false,
                    failureLogLevel: "error",
                });
            }
            if (isVoiceTask(task) && !task.conferenceStarted && task.agentCallId) {
                taskLogger.info(task.toLog(), "Marking conference started");
                this.markConferenceStarted(task);
                this.onCallAccepted(task);
            }
        });

        contact.onMissed(async () => {
            const {
                features: { cleanMissedCalls: isCleanMissedContactsEnabled },
            } = getAppConfigSingleton().getPartnersConfig(this.#partner);

            if (!isCleanMissedContactsEnabled) {
                taskLogger.info(
                    { partner: this.#partner },
                    "Missed contact occurred, cleanMissedCalls not enabled in partner config",
                );
                return;
            }

            if (isChatContact(contact)) {
                await this.handleMissedChat(contact);
            } else {
                await this.handleMissedCall(contact);
            }
        });

        contact.onEnded(() => {
            taskLogger.debug("contact ended event");
            const task = this.getTaskFromStore(contact.contactId);
            const hasWrappingState = useSessionStore.getState().getSessionByTaskId(task.id)?.wrappingState;

            // Guard against duplicate wrapping: onMissed or onError may have already triggered wrapping
            // before onEnded fires, so we check if the task/session is already in wrapping state.
            if (this.isAlreadyInWrappingState(task)) {
                taskLogger.debug(
                    { taskId: task.id, hasWrappingState: !!hasWrappingState },
                    "Skipping duplicate onTaskWrapping",
                );
                return;
            }

            // Notify the UI when the customer's leg drops unexpectedly while the agent is still
            // connected and did not intentionally end/transfer the call. Detection is Connect-specific;
            // the UI layer owns the resulting chime (see useCallDisconnectedChime).
            if (isVoiceTask(task) && shouldPlayDisconnectChime(task, contact, this.#agentEndedContactIds)) {
                taskLogger.info({ taskId: task.id }, "Customer leg dropped unexpectedly - notifying UI");
                this.onCustomerDropped(task);
            }

            void this.onTaskWrapping(task);
        });

        contact.onDestroy(() => {
            taskLogger.debug("contact destroy event");
            this.#agentEndedContactIds.delete(contact.contactId);
            const task = this.getTaskFromStore(contact.contactId);

            // If this contact was a failed outbound callback, keep the session open for summary.
            // task.status is "wrapping" for both success and failure by this point, so we rely on
            // #failedCallbackContactIds (populated in isOutboundCallbackFailure) as the signal instead.
            // Note: this guard requires the cleanMissedCalls feature flag to be enabled.
            // If disabled, onMissed skips isOutboundCallbackFailure entirely, so missed callbacks
            // are never added to the Set and the guard will not fire for them.
            const session = useSessionStore.getState().getSessionByTaskId(task.id);
            const isTrackedFailure = this.#failedCallbackContactIds.delete(task.id);
            if (session?.wrappingState && isTrackedFailure) {
                taskLogger.debug(
                    "Outbound callback failure detected via tracked ID - keeping session open for summary",
                );
                void this.onTaskComplete(task, "CallbackInitiated");
                return;
            }

            const reason = hasPendingCallback() ? "CallbackInitiated" : "SessionEnded";
            void this.onTaskComplete(task, reason);
        });

        contact.onError(() => {
            const task = this.tryGetTaskFromStore(contact.contactId);
            taskLogger.warn({ task, contact }, "Contact error event received");

            if (this.isOutboundCallbackFailure(task, "Contact error during outbound callback")) {
                taskLogger.info("Outbound callback error - triggering session summary");
            }
        });
    }

    private onVoiceIsReady = (isReady: boolean) => {
        AmazonConnectAgentSDK.logger.debug({ isReady }, `===== VOICE SDK IS READY: ${isReady} =====`);
        this.onReady(isReady);
    };

    private getTaskFromStore(taskId: string): AmazonConnectContact {
        const task = useSessionStore.getState().getTask(taskId) as AmazonConnectContact | null;

        if (task == null) throw new Error("Task not found");
        return task;
    }

    /**
     * Non-throwing variant of getTaskFromStore for call sites where a missing task is expected.
     */
    private tryGetTaskFromStore(taskId: string): AmazonConnectContact | undefined {
        const task = useSessionStore.getState().getTask(taskId) as AmazonConnectContact | null;
        return task ?? undefined;
    }

    //#region Chat specific methods

    /**
     * Gets chat messages for a given task and updates the cache.
     *
     * **Note**: For Amazon Connect, messages and events are typically handled automatically so this shouldn't need to be used:
     * - Initial transcript: Fetched via `onConnectionEstablished` → `syncChatTranscript()`
     * - Real-time events: Processed via `onMessage` subscription
     * - Websocket reconnection: Cancels in-flight requests and fetches fresh data on reconnection
     */
    public async getMessages(task: AmazonConnectContact): Promise<ChatMessage[]> {
        assertAmazonConnectChatTask(task, "getMessages");
        const messages = await withConnectMetrics(
            "chat.getMessages",
            () => this.chatSdk.syncChatTranscript(task.config.contact),
            {
                logger: AmazonConnectAgentSDK.logger,
                rethrow: true,
                logResult: false,
                failureLogLevel: "warn",
            },
        );

        return messages ?? [];
    }

    /** Sends a chat message for a given task to the customer */
    public async sendMessage(task: AmazonConnectContact, message: string): Promise<void> {
        assertAmazonConnectChatTask(task, "sendMessage");

        const agentUsername = this.#agent.getConfiguration().username;
        const agentName = this.#agent.getName() || agentUsername;
        const agentId = this.#agent.getAgentARN();
        const employeeId = userCache.employeeId || this.employeeNum;

        if (!agentName || !agentId || !employeeId) {
            throw new Error(
                `Cannot send message: agent name, ID, or employee ID not available for contact ${task.id} (name: ${agentName}, id: ${agentId}, employee ID: ${employeeId})`,
            );
        }

        await withConnectMetrics(
            "chat.sendMessage",
            () =>
                this.chatSdk.sendMessage(task, message, {
                    agent: this.#agent,
                    agentId,
                    author: employeeId,
                    senderName: agentName,
                    partner: task.partner,
                    sourceApp: task.sourceApp,
                    topic: task.topic,
                    language: task.language,
                }),
            {
                logger: AmazonConnectAgentSDK.logger,
                rethrow: true,
                logResult: false,
                failureLogLevel: "error",
                // A send into an already-ended chat is an expected teardown race, not a failure:
                // count it as cancelled so it doesn't inflate the chat.sendMessage Failure metric.
                shouldCountAsCancelled: (error) => error instanceof ContactEndedError,
            },
        );
    }

    /** Sends a typing indicator to the customer for a given task */
    public async sendTypingIndicator(task: AmazonConnectContact): Promise<void> {
        assertAmazonConnectChatTask(task, "sendTypingIndicator");
        await this.chatSdk.sendTypingIndicator(task.config.contact);
    }

    /**
     * Advances the read status of chat messages for a task. Only supports marking all messages as read.
     */
    public async advanceReadStatus(task: AmazonConnectContact, options: AdvanceReadStatusOptions): Promise<void> {
        assertAmazonConnectChatTask(task, "advanceReadStatus");

        await withConnectMetrics(
            "chat.advanceReadStatus",
            async () => {
                if (options.allMessagesRead) {
                    await this.chatSdk.setAllMessagesRead(task.config.contact);
                } else if (options.lastReadMessageIndex !== undefined) {
                    AmazonConnectAgentSDK.logger.warn(
                        "lastReadMessageIndex is deprecated running allMessagesRead instead.",
                    );
                    await this.chatSdk.setAllMessagesRead(task.config.contact);
                } else if (options.allMessagesUnread) {
                    AmazonConnectAgentSDK.logger.warn("allMessagesUnread is not supported by the connect provider.");
                }
            },
            {
                logger: AmazonConnectAgentSDK.logger,
                rethrow: true,
                logResult: false,
                failureLogLevel: "warn",
            },
        );
    }
    //#endregion
}

function withToMetrics(
    rawMetricsHandler: (metricsData: ExWoMetricData[], sendProps?: MetricsToServerProps) => Promise<void>,
    attempt: number,
) {
    const metricsHandler = (metricsData: ExWoMetricData[], sendProps?: MetricsToServerProps) =>
        rawMetricsHandler(
            metricsData.map((data) => ({
                ...data,
                dimensions: { ...data.dimensions, ...defaultConnectMetricsDimensions },
            })),
            sendProps,
        );

    void metricsHandler([
        {
            metrics: [{ name: "Start", value: 1 }],
            dimensions: { action: "connect.auth" },
        },
        ...(attempt
            ? [
                  {
                      metrics: [{ name: "Retry", value: attempt }],
                      dimensions: { action: "connect.init" },
                  },
              ]
            : []),
    ]);
    connect.core.onAuthorizeSuccess(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Success", value: 1 }],
                    dimensions: { action: "connect.auth" },
                },
            ]),
    );

    connect.core.onAuthFail(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Failure", value: 1 }],
                    dimensions: { action: "connect.auth" },
                },
            ]),
    );

    connect.core.onAuthorizeRetriesExhausted(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Exhausted", value: 1 }],
                    dimensions: { action: "connect.auth" },
                },
            ]),
    );

    connect.core.onAccessDenied(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Failure", value: 1 }],
                    dimensions: { action: "connect.access" },
                },
            ]),
    );

    connect.core.onInitialized(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Success", value: 1 }],
                    dimensions: { action: "connect.init" },
                },
            ]),
    );

    connect.core.onIframeRetriesExhausted(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Exhausted", value: 1 }],
                    dimensions: { action: "connect.init" },
                },
            ]),
    );

    connect.globalResiliency.onFailoverPending(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Event", value: 1 }],
                    dimensions: { action: "connect.failover.pending" },
                },
            ]),
    );

    connect.globalResiliency.onFailoverCompleted(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Event", value: 1 }],
                    dimensions: { action: "connect.failover.completed" },
                },
            ]),
    );

    connect.globalResiliency.onConfigureError(
        () =>
            void metricsHandler([
                {
                    metrics: [{ name: "Error", value: 1 }],
                    dimensions: { action: "connect.failover.configure" },
                },
            ]),
    );

    connect.agent((agent) => {
        void metricsHandler([
            {
                metrics: [{ name: "Success", value: 1 }],
                dimensions: { action: "connect.agent.initialized" },
            },
        ]);

        agent.onError(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Error", value: 1 }],
                        dimensions: { action: "connect.agent" },
                    },
                ]),
        );

        agent.onSoftphoneError(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Error", value: 1 }],
                        dimensions: { action: "connect.agent.softphone" },
                    },
                ]),
        );

        agent.onWebSocketConnectionGained(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Event", value: 1 }],
                        dimensions: { action: "connect.agent.websocket.connected" },
                    },
                ]),
        );

        agent.onWebSocketConnectionLost(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Event", value: 1 }],
                        dimensions: { action: "connect.agent.websocket.disconnected" },
                    },
                ]),
        );
    });

    connect.contact((contact) => {
        void metricsHandler([
            {
                metrics: [{ name: "Event", value: 1 }],
                dimensions: { action: "connect.contact.received" },
            },
        ]);

        contact.onMissed(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Event", value: 1 }],
                        dimensions: { action: "connect.contact.missed" },
                    },
                ]),
        );

        contact.onError(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Event", value: 1 }],
                        dimensions: { action: "connect.contact.error" },
                    },
                ]),
        );

        contact.onDestroy(
            () =>
                void metricsHandler([
                    {
                        metrics: [{ name: "Event", value: 1 }],
                        dimensions: { action: "connect.contact.destroy" },
                    },
                ]),
        );
    });
}
